import {
  Stack, Group, Grid, Text, Anchor, Code, Table, Card, Button, Textarea,
  Divider, Badge, Skeleton, Menu,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChevronDown } from '@tabler/icons-react';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { cardType, type ClosedReason } from '@taksikortti/shared';
import { useDuplicateDetail, useApplyDuplicateAction, type DuplicateAction } from '../api/duplicates';
import { CardTypeBadge } from '../components/CardTypeBadge';
import { StateBadge } from '../components/StateBadge';
import { InvoiceBadge } from '../components/InvoiceBadge';
import { ErrorState } from '../components/ErrorState';
import { ApiError } from '../api/client';
import { apiErrorMessage } from '../lib/errors';
import { t } from '../lib/strings';
import { eur } from '../lib/format';
import { formatDateTime, formatRelative } from '../lib/dates';

/**
 * Duplicate-group detail — SAME layout + workflow as the kertakortti case view:
 * customer header, left summary card with the Kirjaa kysely → Kirjaa muistutus →
 * Sulje (syyllä) ladder + inline notes, right pane with the charges + timeline.
 */
export function DuplicateDetailPage() {
  const params = useParams({ from: '/cases/tupla/$korttinumero/$groupId' });
  const { data, isLoading, isError, error, refetch } = useDuplicateDetail(params.korttinumero, params.groupId);
  const apply = useApplyDuplicateAction();

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={24} width={160} />
        <Skeleton height={40} width={260} />
        <Skeleton height={280} radius="md" />
      </Stack>
    );
  }
  if (isError || !data) {
    return (
      <Stack gap="md">
        <Anchor component={Link} to="/cases">{t.dupDetail.backToList}</Anchor>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Stack>
    );
  }

  const g = data.group;
  const status = g.reviewStatus;

  function run(action: DuplicateAction): void {
    apply.mutate(
      { korttinumero: g.korttinumero, groupId: g.groupId, etag: g.etag, action },
      {
        onError: (e) => notifications.show({
          color: e instanceof ApiError && e.body.code === 'stale_version' ? 'orange' : 'red',
          message: apiErrorMessage(e),
        }),
        onSuccess: () => {
          // edit_comment has its own inline "Tallennettu" flash — don't double up.
          if (action.kind !== 'edit_comment') notifications.show({ color: 'green', message: t.caseDetail.actionDone });
        },
      },
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Anchor component={Link} to="/cases">{t.dupDetail.backToList}</Anchor>
        <CardTypeBadge type={cardType(g.korttinumero)} />
      </Group>

      <Group gap="sm" align="center" wrap="nowrap">
        <Code fz="lg" fw={700}>{g.korttinumero}</Code>
        <StateBadge status={status} closedReason={g.closedReason ?? null} />
        <Text fw={600} size="lg" truncate>{g.asiakas ?? g.asiakasnumero ?? '—'}</Text>
      </Group>

      <Grid>
        {/* Left pane — case-style summary + actions */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Card withBorder>
            <Stack gap="sm">
              <Text size="sm" fw={500}>Tämä tapaus</Text>
              <Text size="sm">
                {t.dupDetail.summary(g.count, g.refundCount, eur(g.totalAmount))}
                {g.palvelutunnus ? ` · ${g.palvelutunnus}` : ''}
              </Text>
              <Group gap="xs">
                <Badge color="green" variant="light">{t.invoice.billedTimes(g.invoicedCount ?? 0)}</Badge>
                <Badge color="blue" variant="light">{t.invoice.paidTimes(g.paidCount ?? 0)}</Badge>
              </Group>
              {status === 'dismissed' && g.reviewedBy === 'system' && (
                <Text size="xs" c="dimmed">{t.invoice.autoResolved}</Text>
              )}
              <FieldRow label="Ensim. veloitus" value={formatDateTime(g.firstAt)} />
              <FieldRow label="Viim. veloitus" value={formatDateTime(g.lastAt)} />
              <Divider />

              {/* Unified ladder: Kirjaa kysely → Kirjaa muistutus → Sulje (syyllä). */}
              {(status === 'pending' || status === 'review_required') && (
                <Button variant="light" loading={apply.isPending} onClick={() => run({ kind: 'send_query' })}>
                  {t.caseDetail.tripActions.send_query}
                </Button>
              )}
              {status === 'query_sent' && (
                <Button variant="light" loading={apply.isPending} onClick={() => run({ kind: 'send_reminder' })}>
                  {t.caseDetail.tripActions.send_reminder}
                </Button>
              )}
              {status !== 'dismissed' && (
                <Menu shadow="md">
                  <Menu.Target>
                    <Button variant="light" color="gray" loading={apply.isPending} rightSection={<IconChevronDown size={14} />}>
                      {t.caseDetail.closeCase}
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {(['resolved', 'no_reply', 'not_applicable'] as ClosedReason[]).map((reason) => (
                      <Menu.Item key={reason} onClick={() => run({ kind: 'dismiss', reason })}>
                        {t.closedReason[reason]}
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
              )}
              {status === 'dismissed' && (
                <Button variant="light" loading={apply.isPending} onClick={() => run({ kind: 'reopen' })}>
                  {t.caseDetail.reopen}
                </Button>
              )}
              <Divider />
              <CommentEditor
                key={g.groupId + (g.comment ?? '')}
                initial={g.comment ?? ''}
                saving={apply.isPending}
                onSave={(comment) => run({ kind: 'edit_comment', comment })}
              />
            </Stack>
          </Card>
        </Grid.Col>

        {/* Right pane — charges + timeline */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            <Card withBorder>
              <Stack gap="xs">
                <Text fw={500}>{t.dupDetail.members}</Text>
                <Table.ScrollContainer minWidth={700}>
                  <Table withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t.duplicates.member.time}</Table.Th>
                        <Table.Th>{t.duplicates.member.receipt}</Table.Th>
                        <Table.Th ta="right">{t.duplicates.member.amount}</Table.Th>
                        <Table.Th>{t.invoice.laskutus}</Table.Th>
                        <Table.Th>{t.invoice.tilitys}</Table.Th>
                        <Table.Th>{t.duplicates.member.driver}</Table.Th>
                        <Table.Th>{t.duplicates.member.palvelutunnus}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {g.members.map((m) => (
                        <Table.Tr key={m.dedupKey}>
                          <Table.Td>{formatDateTime(m.matkanAlkamisaika)}</Table.Td>
                          <Table.Td>{m.kuittinumero}</Table.Td>
                          <Table.Td ta="right">{eur(m.hinta)} €</Table.Td>
                          <Table.Td><InvoiceBadge status={m.laskutustila} /></Table.Td>
                          <Table.Td><InvoiceBadge status={m.tilitystila} /></Table.Td>
                          <Table.Td>{m.kuljettaja ?? '—'}</Table.Td>
                          <Table.Td>{m.palvelutunnus ? <Code>{m.palvelutunnus}</Code> : '—'}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Stack>
            </Card>

            <Card withBorder>
              <Stack gap="xs">
                <Text fw={500}>{t.dupDetail.timeline}</Text>
                {data.timeline.length === 0 ? (
                  <Text c="dimmed" size="sm">{t.dupDetail.noEvents}</Text>
                ) : (
                  data.timeline.map((ev) => (
                    <Group key={ev.inverseTimestamp} gap="sm" wrap="nowrap">
                      <Text size="xs" c="dimmed" w={140}>{formatRelative(ev.at)}</Text>
                      <Text size="sm"><b>{ev.actor}</b> · {describeDupEvent(ev.kind)}</Text>
                    </Group>
                  ))
                )}
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between">
      <Text size="sm" c="dimmed">{label}</Text>
      <Text size="sm">{value}</Text>
    </Group>
  );
}

/** Inline notes editor — same pattern as the case view's CaseNotesEditor. */
function CommentEditor({
  initial, onSave, saving,
}: {
  initial: string;
  onSave: (comment: string) => void;
  saving: boolean;
}) {
  const [value, setValue] = useState(initial);
  const [savedFlash, setSavedFlash] = useState(false);
  const dirty = value !== initial;

  useEffect(() => {
    if (!saving && !dirty && savedFlash) {
      const timer = setTimeout(() => setSavedFlash(false), 1500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [saving, dirty, savedFlash]);

  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>{t.caseDetail.caseNotes}</Text>
      <Textarea
        value={value}
        onChange={(e) => setValue(e.currentTarget.value)}
        placeholder={t.caseDetail.caseNotesPlaceholder}
        aria-label={t.caseDetail.caseNotes}
        autosize
        minRows={3}
      />
      <Group justify="flex-end" gap="xs">
        {savedFlash && !dirty && (
          <Text size="xs" c="green">{t.caseDetail.caseNotesSaved}</Text>
        )}
        <Button
          size="compact-sm"
          variant="light"
          disabled={!dirty}
          loading={saving && dirty}
          onClick={() => { onSave(value); setSavedFlash(true); }}
        >
          {t.caseDetail.caseNotesSave}
        </Button>
      </Group>
    </Stack>
  );
}

function describeDupEvent(kind: string): string {
  switch (kind) {
    case 'dup_query_sent': return 'kirjasi kyselyn';
    case 'dup_reminder_sent': return 'kirjasi muistutuksen';
    case 'note_added': return 'lisäsi muistiinpanon';
    case 'dup_marked_review': return 'merkitsi tarkistettavaksi';
    case 'dup_dismissed': return 'sulki tapauksen';
    case 'dup_reopened': return 'avasi uudelleen';
    case 'dup_reassigned': return 'vaihtoi vastuuhenkilön';
    default: return 'tapahtuma';
  }
}
